import { useState } from "react"
import { Link } from "@tanstack/react-router"
import {
	IconCircleDashedOutline18 as CircleDot,
	IconCodeCommitOutline18 as GitCommit,
	IconCodePullRequestOutline18 as GitPullRequest,
	IconMsgOutline18 as MessageIcon,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"
import { Button } from "@productlane/ui"

import type { SuggestionSource } from "./aggregate-proposals"

export const COLLAPSED_SOURCE_COUNT = 4

export function visibleSources(
	sources: readonly SuggestionSource[],
	expanded: boolean,
): { shown: readonly SuggestionSource[]; hiddenCount: number } {
	if (expanded || sources.length <= COLLAPSED_SOURCE_COUNT) {
		return { shown: sources, hiddenCount: 0 }
	}
	return {
		shown: sources.slice(0, COLLAPSED_SOURCE_COUNT),
		hiddenCount: sources.length - COLLAPSED_SOURCE_COUNT,
	}
}

const chipClass = cn(
	"inline-flex min-w-0 max-w-full items-center gap-1 rounded-md px-1.5 py-1",
	"text-[11px] leading-none text-gray-700 dark:text-gray-300",
	"bg-gray-100 ring-1 ring-inset ring-black/5 dark:bg-gray-800 dark:ring-white/5",
	"hover:bg-gray-200 hover:text-gray-900",
	"dark:hover:bg-gray-750 dark:hover:text-gray-100",
)

function SourceIcon({ kind }: { kind: SuggestionSource["kind"] }) {
	const Icon =
		kind === "pr"
			? GitPullRequest
			: kind === "commit"
				? GitCommit
				: kind === "thread"
					? MessageIcon
					: CircleDot
	return <Icon aria-hidden className="h-3 w-3 flex-none" strokeWidth={2} />
}

function SourceChip({ source }: { source: SuggestionSource }) {
	const content = (
		<>
			<SourceIcon kind={source.kind} />
			<span
				className={cn(
					"min-w-0 truncate text-xxs",
					source.kind !== "thread" && "font-mono",
				)}
			>
				{source.label}
			</span>
		</>
	)
	// Threads open the in-app conversation; the rest are GitHub or Linear URLs.
	if (source.kind === "thread") {
		return (
			<Link to={source.url} title={source.label} className={chipClass}>
				{content}
			</Link>
		)
	}
	return (
		<a
			href={source.url}
			target="_blank"
			rel="noreferrer"
			title={source.label}
			className={chipClass}
		>
			{content}
		</a>
	)
}

export function ProposalSources({
	sources,
}: {
	sources: readonly SuggestionSource[]
}) {
	const [expanded, setExpanded] = useState(false)
	if (sources.length === 0) return null
	const { shown, hiddenCount } = visibleSources(sources, expanded)
	const canCollapse = expanded && sources.length > COLLAPSED_SOURCE_COUNT

	return (
		<div
			className="mb-3 flex flex-wrap items-center gap-1"
			data-proposal-sources=""
		>
			{shown.map((source) => (
				<SourceChip key={source.url} source={source} />
			))}
			{(hiddenCount > 0 || canCollapse) && (
				<Button
					variant="ghost"
					size="xs"
					onClick={() => setExpanded((open) => !open)}
				>
					{hiddenCount > 0 ? `${hiddenCount} more` : "Show less"}
				</Button>
			)}
		</div>
	)
}
